/**
 * Shape of the react-router `location.state` this app attaches to
 * in-app navigations (STIC-15588).
 */
export interface NavigationState {
  /**
   * Set by sidebar-menu navigations (NavList, SidebarBody, header/sidebar
   * logos): marks the target as a top-level entry. BackHistory hides the
   * back arrow there and AppLayout resets the history-depth stack, so
   * only drill-ins from that page show the arrow.
   */
  menuRoot?: boolean;
  /**
   * Set by in-content click-throughs that land on a page which is itself a
   * top-level/menu page (e.g. dashboard widget → its list view). Those pages
   * statically opt out of the back arrow (isShowBackHistory=false), but when
   * reached this way the user came from somewhere and needs a way back, so
   * this flag opts the arrow back in for that single history entry. Ignored
   * when menuRoot is set (a genuine sidebar-menu click always wins → hidden).
   */
  showBack?: boolean;
}

/** State to pass on `history.push` for sidebar-menu/root navigations. */
export const MENU_ROOT_STATE: NavigationState = { menuRoot: true };

/**
 * State to pass on `history.push` for content click-throughs (e.g. dashboard
 * widgets) that should force the back arrow on an otherwise top-level target.
 */
export const SHOW_BACK_STATE: NavigationState = { showBack: true };

/** Whether the current history entry was reached via a sidebar-menu click. */
export const isMenuRootEntry = (location: { state?: unknown }): boolean =>
  Boolean((location.state as NavigationState | undefined)?.menuRoot);

/** Whether the current history entry explicitly requested the back arrow. */
export const isShowBackEntry = (location: { state?: unknown }): boolean =>
  Boolean((location.state as NavigationState | undefined)?.showBack);
